import { LanguageMultiSelect } from '@/components/auth/LanguageMultiSelect';
import { useLanguages } from '@/hooks/use-languages';
import { getRequiredLanguageFields } from '@/lib/language-profile';
import type { UserRole } from '@/types/auth.types';
import { SUPPORTED_LANGUAGES } from '@/lib/signup.constants';

export type LanguagePreferencesValue = {
  spokenLanguages: string[];
  contributionLanguages: string[];
  moderationLanguages: string[];
};

type LanguagePreferencesCopy = {
  spokenLanguages: string;
  contributionLanguages: string;
  moderationLanguages: string;
  selectLanguagesSpoken: string;
  selectLanguagesContribute: string;
  selectLanguagesModerate: string;
  noLanguageSelected: string;
  xSelected: string;
  searchLanguage: string;
  noLanguageFound: string;
};

type LanguagePreferencesFieldsProps = {
  role?: UserRole;
  value: LanguagePreferencesValue;
  onChange: (value: LanguagePreferencesValue) => void;
  copy: LanguagePreferencesCopy;
  disabled?: boolean;
  /** Stack the fields in one column, for narrow containers such as the auth forms. */
  stacked?: boolean;
};

export function LanguagePreferencesFields({
  role,
  value,
  onChange,
  copy,
  disabled = false,
  stacked = false,
}: LanguagePreferencesFieldsProps) {
  const { activeLanguages } = useLanguages();
  const requiredFields = getRequiredLanguageFields(role);
  const options = activeLanguages.length
    ? activeLanguages.map((language) => ({
        value: language.code,
        label: language.displayName || language.name || language.code,
        flag: language.flag || '🌍',
      }))
    : [...SUPPORTED_LANGUAGES];
  const knownCodes = new Set(options.map((option) => option.value));
  const selectedCodes = Array.from(
    new Set([
      ...value.spokenLanguages,
      ...value.contributionLanguages,
      ...value.moderationLanguages,
    ]),
  );
  for (const code of selectedCodes) {
    if (!knownCodes.has(code)) {
      options.push({ value: code, label: code.toUpperCase(), flag: '🌍' });
    }
  }

  const renderField = (
    field: keyof LanguagePreferencesValue,
    label: string,
    placeholder: string,
  ) => (
    <LanguageMultiSelect
      label={`${label}${requiredFields.includes(field) ? ' *' : ''}`}
      placeholder={placeholder}
      emptyLabel={copy.noLanguageSelected}
      selectedSuffix={copy.xSelected}
      searchPlaceholder={copy.searchLanguage}
      emptySearchLabel={copy.noLanguageFound}
      options={options}
      values={value[field]}
      disabled={disabled}
      onToggle={(languageCode) => {
        const current = value[field];
        onChange({
          ...value,
          [field]: current.includes(languageCode)
            ? current.filter((code) => code !== languageCode)
            : [...current, languageCode],
        });
      }}
    />
  );

  return (
    <div className={stacked ? "grid grid-cols-1 gap-5" : "grid gap-5 lg:grid-cols-2"}>
      {renderField(
        'spokenLanguages',
        copy.spokenLanguages,
        copy.selectLanguagesSpoken,
      )}
      {renderField(
        'contributionLanguages',
        copy.contributionLanguages,
        copy.selectLanguagesContribute,
      )}
      {role === 'moderator'
        ? renderField(
            'moderationLanguages',
            copy.moderationLanguages,
            copy.selectLanguagesModerate,
          )
        : null}
    </div>
  );
}
